import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import {
  getPrescriptionConfig,
  updatePrescriptionConfig,
  getPrescriptionThemes,
  uploadPrescriptionLogo,
  getLogoPreviewUrl,
  uploadPrescriptionLetterhead,
  updateLetterheadOffset,
  uploadCustomTemplate,
  getTemplatePreview,
  getVariantPreview,
  applyLetterheadSource,
} from '../../services/api/prescriptionBrandingApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * PrescriptionBrandingSettings — Admin page for configuring prescription
 * template themes, accent colors, logo, fonts, and custom HTML templates.
 * ═══════════════════════════════════════════════════════════════════════
 */

const THEME_PREVIEWS = {
  classic: { gradient: 'linear-gradient(135deg, #0d4f8b 0%, #1a6fc4 100%)', emoji: '🏥' },
  modern: { gradient: 'linear-gradient(135deg, #2d3436 0%, #636e72 100%)', emoji: '✨' },
  compact: { gradient: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', emoji: '📄' },
  custom: { gradient: 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)', emoji: '🎨' },
};

const PAGE_SIZES = [
  { value: 'A5', label: 'A5 (148×210mm)', desc: 'Half-A4, common for Indian OPD pads' },
  { value: 'A4', label: 'A4 (210×297mm)', desc: 'Standard full-size sheet' },
  { value: 'LETTER', label: 'Letter (8.5×11")', desc: 'US standard' },
];

const FONT_OPTIONS = [
  { value: 'Segoe UI, Arial, Helvetica, sans-serif', label: 'Segoe UI (Default)' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, Times New Roman, serif', label: 'Georgia (Serif)' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
];

/** Safely extract a renderable string from FastAPI error responses. */
function parseApiError(err, fallback = 'Something went wrong.') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  // Pydantic 422 validation errors: [{type, loc, msg, input}, ...]
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || JSON.stringify(e)).join('; ');
  }
  return fallback;
}

// Ratified PO copy (stories.md § Copy list) — never free-hand this string.
const BRANDING_SECTION_SURGERIES_TOOLTIP =
  'Turning this off only hides advised surgeries on the printed prescription; they are still recorded on the visit.';

// Matches backend `get_default_sections()` order exactly (app/schemas/prescription_composer.py) —
// surgeries sits between report (Tests) and notes (Instructions).
const DEFAULT_SECTIONS = [
  { id: 'vitals', enabled: true, order: 1, label: 'Vitals' },
  { id: 'symptoms', enabled: true, order: 2, label: 'Patient Concerns / Symptoms' },
  { id: 'diagnosis', enabled: true, order: 3, label: 'Diagnosis' },
  { id: 'medicines', enabled: true, order: 4, label: 'Rx Medicines' },
  { id: 'report', enabled: true, order: 5, label: 'Tests To Be Done' },
  { id: 'surgeries', enabled: true, order: 6, label: 'Advised surgeries' },
  { id: 'notes', enabled: true, order: 7, label: 'Instructions / Notes' },
  { id: 'follow_up', enabled: true, order: 8, label: 'Follow-Up' },
];

export default function PrescriptionBrandingSettings() {
  const { isDark } = useTheme();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [themes, setThemes] = useState([]);
  const [capabilities, setCapabilities] = useState({});
  const [config, setConfig] = useState({
    theme: 'classic',
    accent_color: '#0d4f8b',
    font_family: 'Segoe UI, Arial, Helvetica, sans-serif',
    page_size: 'A4',
    paper_mode: 'normal',
    show_header: true,
    show_doctor_header: true,
    show_patient_banner: true,
    show_footer: true,
    margins: { top: 6, bottom: 6, left: 7, right: 7 },
    sections: DEFAULT_SECTIONS,
    clinic_logo_s3_key: null,
    clinic_name: '',
    clinic_description: '',
    clinic_address: '',
    clinic_phone: '',
    clinic_email: '',
    clinic_pincode: '',
    show_section_backgrounds: true,
  });

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(null);
  const previewFrameRef = useRef(null);

  // Logo preview (blob URL from backend proxy — no S3 URL exposed)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

  // File upload
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLetterhead, setUploadingLetterhead] = useState(false);
  const [uploadingHtmlTemplate, setUploadingHtmlTemplate] = useState(false);
  const [letterheadGenerationMethod, setLetterheadGenerationMethod] = useState(null);
  const [letterheadMetadata, setLetterheadMetadata] = useState(null);

  // HTML source view / edit
  const [letterheadHtml, setLetterheadHtml] = useState(null);
  const [letterheadHtmlEdited, setLetterheadHtmlEdited] = useState(null);
  const [debouncedHtml, setDebouncedHtml] = useState(null); // 300ms behind edited — drives live preview
  const [previewTab, setPreviewTab] = useState('preview'); // 'preview' | 'html'
  const [applyingHtml, setApplyingHtml] = useState(false);

  // Static template contract state
  const [uploadErrors, setUploadErrors] = useState([]); // structured errors from upload endpoint
  const [uploadWarnings, setUploadWarnings] = useState([]); // warnings (e.g. small content box)
  const [contentBox, setContentBox] = useState(null); // { left_mm, top_mm, width_mm, height_mm }
  const isDragUpdateRef = useRef(false); // true after drag — skip iframe reload so drag feels smooth

  // Style variant picker — shown after pipeline upload
  const [templateVariants, setTemplateVariants] = useState([]); // [{variant_id, label, description, s3_key?}]
  const [selectedVariantId, setSelectedVariantId] = useState(null); // variant_id of the confirmed choice
  const [pendingVariantId, setPendingVariantId] = useState(null); // non-standard variant chosen but not yet saved


  // ── Load config + themes + logo preview ───────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [configRes, themesRes] = await Promise.all([
          getPrescriptionConfig(),
          getPrescriptionThemes(),
        ]);
        if (configRes.capabilities) {
          setCapabilities(configRes.capabilities);
        }
        if (configRes.config && Object.keys(configRes.config).length > 0) {
          setConfig(prev => ({
            ...prev,
            ...configRes.config,
            margins: configRes.config.margins || prev.margins,
            sections: (configRes.config.sections && configRes.config.sections.length > 0)
              ? configRes.config.sections.map(s => ({
                  ...s,
                  label: DEFAULT_SECTIONS.find(d => d.id === s.id)?.label || s.id,
                }))
              : prev.sections,
          }));
          // Restore content box display for saved static templates
          if (configRes.config.content_box) {
            setContentBox(configRes.config.content_box);
          }
        }
        setThemes(themesRes.themes || []);

        // Load logo preview via backend proxy
        const logoUrl = await getLogoPreviewUrl();
        if (logoUrl) setLogoPreviewUrl(logoUrl);
      } catch (err) {
        console.error('[Branding] Load failed:', err);
        setError('Failed to load prescription settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce HTML edits so live preview updates 300ms after typing.
  // Skip reload when the change came from a drag (iframe already has the right DOM).
  useEffect(() => {
    if (isDragUpdateRef.current) {
      isDragUpdateRef.current = false;
      return;
    }
    const timer = setTimeout(() => setDebouncedHtml(letterheadHtmlEdited), 300);
    return () => clearTimeout(timer);
  }, [letterheadHtmlEdited]);

  // Listen for drag-position updates posted from the live preview iframe
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data?.type !== 'ak-drag-update') return;
      isDragUpdateRef.current = true;
      setLetterheadHtmlEdited(e.data.html);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ── Preview loader ────────────────────────────────────
  const loadPreview = useCallback(async (theme, s3Key) => {
    try {
      setPreviewLoading(true);
      setPreviewTheme(theme);
      const html = await getTemplatePreview(theme, s3Key);
      setPreviewHtml(html);
    } catch (err) {
      console.error('[Branding] Preview failed:', err);
      setPreviewHtml('<p style="color:red;padding:20px;">Preview failed to load.</p>');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // ── Save config ───────────────────────────────────────
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const currentCaps = capabilities[config.theme] || {
        supports_letterhead_margins: config.theme === 'custom',
        supports_section_reordering: config.theme === 'custom',
        supports_paper_mode: config.theme === 'custom',
      };

      const {
        theme, accent_color, font_family, page_size, paper_mode, show_header, show_doctor_header, show_patient_banner, show_footer, margins, sections,
        clinic_logo_s3_key, custom_template_s3_key,
        clinic_name, clinic_description, clinic_address, clinic_phone, clinic_email, clinic_pincode,
      } = config;

      const payload = { theme, accent_color, font_family, page_size };
      if (currentCaps.supports_paper_mode) {
        payload.paper_mode = paper_mode || 'normal';
        payload.show_header = show_header !== false;
        payload.show_doctor_header = show_doctor_header !== false;
        payload.show_patient_banner = show_patient_banner !== false;
        payload.show_footer = show_footer !== false;
      }
      if (currentCaps.supports_letterhead_margins && margins) {
        payload.margins = {
          top: Number(margins.top ?? 6),
          bottom: Number(margins.bottom ?? 6),
          left: Number(margins.left ?? 7),
          right: Number(margins.right ?? 7),
        };
      }
      if (currentCaps.supports_section_reordering && sections) {
        payload.sections = sections.map(s => ({
          id: s.id,
          enabled: Boolean(s.enabled),
          order: Number(s.order),
          title_override: s.title_override?.trim() ? s.title_override.trim() : null,
        }));
      }

      if (clinic_logo_s3_key) payload.clinic_logo_s3_key = clinic_logo_s3_key;
      if (custom_template_s3_key) payload.custom_template_s3_key = custom_template_s3_key;
      if (clinic_name?.trim()) payload.clinic_name = clinic_name.trim();
      if (clinic_description?.trim()) payload.clinic_description = clinic_description.trim();
      if (clinic_address?.trim()) payload.clinic_address = clinic_address.trim();
      if (clinic_phone?.trim()) payload.clinic_phone = clinic_phone.trim();
      if (clinic_email?.trim()) payload.clinic_email = clinic_email.trim();
      if (clinic_pincode?.trim()) payload.clinic_pincode = clinic_pincode.trim();

      const saveResult = await updatePrescriptionConfig(payload);
      const savedKey = saveResult?.config?.custom_template_s3_key;
      if (pendingVariantId && savedKey) {
        setConfig(prev => ({ ...prev, custom_template_s3_key: savedKey }));
        loadPreview('custom', savedKey);
        setTemplateVariants([]);
        setPendingVariantId(null);
      }
      setSuccess('Prescription branding saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[Branding] Save failed:', err);
      setError(parseApiError(err, 'Failed to save settings.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Logo upload ───────────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2MB.');
      return;
    }
    try {
      setUploadingLogo(true);
      setError(null);
      const result = await uploadPrescriptionLogo(file);
      setConfig(prev => ({ ...prev, clinic_logo_s3_key: result.s3_key }));
      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
      // Refresh logo preview — done after success toast so upload failure doesn't mask success
      try {
        if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
        const newUrl = await getLogoPreviewUrl();
        setLogoPreviewUrl(newUrl);
      } catch (_previewErr) {
        // Preview refresh failed — logo was uploaded, just not displayable yet
      }
    } catch (err) {
      setError(parseApiError(err, 'Failed to upload logo.'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // ── Letterhead upload ─────────────────────────────────
  const handleLetterheadUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Letterhead image must be under 5MB.');
      return;
    }
    try {
      setUploadingLetterhead(true);
      setError(null);
      const result = await uploadPrescriptionLetterhead(file);
      setLetterheadGenerationMethod(result.generation_method ?? 'fallback');
      if (result.analysis?.doctor_metadata) {
        setLetterheadMetadata(result.analysis.doctor_metadata);
      }
      if (result.generated_html) {
        setLetterheadHtml(result.generated_html);
        setLetterheadHtmlEdited(result.generated_html);
      }
      setPreviewTab('preview');
      setConfig(prev => ({
        ...prev,
        theme: 'custom',
        custom_template_s3_key: result.custom_template_s3_key,
        letterhead_raw_s3_key: result.letterhead_raw_s3_key,
        letterhead_top_offset_mm: result.top_offset_mm ?? 70,
        letterhead_left_offset_mm: result.left_offset_mm ?? 7,
        // Lifecycle fields — passed through to PUT /prescription-config on Save Branding
        ...(result.template_renderer_version && { template_renderer_version: result.template_renderer_version }),
        ...(result.template_pipeline_version && { template_pipeline_version: result.template_pipeline_version }),
        ...(result.template_geometry_s3_key  && { template_geometry_s3_key: result.template_geometry_s3_key }),
      }));
      // Only offer variants that already have an uploaded s3_key (the "standard" style).
      // Non-standard variants (e.g. Professional, Minimal) rely on the backend building
      // + uploading the HTML on Save — that half of the pipeline isn't deployed, so
      // picking one would silently keep the old template. Hide them from the gallery.
      const standardVariants = (result.template_variants || []).filter((v) => v.s3_key);
      if (standardVariants.length > 0) {
        // Pipeline path — show thumbnail picker; auto-load the standard variant into main preview
        setTemplateVariants(standardVariants);
        setSelectedVariantId('standard');
        loadPreview('custom', result.custom_template_s3_key);
      } else {
        // Fallback/gpt4o path — existing single-preview behaviour
        const msg = ['pipeline', 'gpt4o'].includes(result.generation_method)
          ? 'Template generated by AI — review the preview below, then click Save Branding.'
          : 'Letterhead uploaded! Adjust the offset if needed, then click Save Branding.';
        setSuccess(msg);
        setTimeout(() => setSuccess(null), 5000);
        loadPreview('custom', result.custom_template_s3_key);
      }
    } catch (err) {
      setError(parseApiError(err, 'Failed to upload letterhead.'));
    } finally {
      setUploadingLetterhead(false);
      e.target.value = '';
    }
  };

  // ── Manual HTML template upload ───────────────────────
  const handleHtmlTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingHtmlTemplate(true);
      setError(null);
      setUploadErrors([]);
      setUploadWarnings([]);
      setContentBox(null);
      const htmlContent = await file.text();
      const result = await uploadCustomTemplate(file);

      // Structured validation errors from static_fill_content contract
      if (result.valid === false && result.errors?.length) {
        setUploadErrors(result.errors);
        setError('Template has validation errors. See details below.');
        return;
      }

      // Warnings (non-blocking)
      if (result.warnings?.length) setUploadWarnings(result.warnings);

      // Content box — static_fill_content only
      if (result.template_mode === 'static_fill_content' && result.content_box) {
        setContentBox(result.content_box);
      }

      const isStatic = result.template_mode === 'static_fill_content';
      setLetterheadGenerationMethod('manual');
      setTemplateVariants([]);
      setPreviewTab('preview');

      if (!isStatic) {
        // Legacy: keep HTML source tab available
        setLetterheadHtml(htmlContent);
        setLetterheadHtmlEdited(htmlContent);
      } else {
        // Static: no HTML editing — template is a background artifact
        setLetterheadHtml(null);
        setLetterheadHtmlEdited(null);
      }

      setLetterheadMetadata(null);
      setConfig(prev => ({
        ...prev,
        theme: 'custom',
        custom_template_s3_key: result.s3_key,
        template_mode: result.template_mode,
        content_box: result.content_box ?? null,
        template_contract_hash: result.contract_hash ?? null,
        letterhead_raw_s3_key: null,
      }));
      loadPreview('custom', result.s3_key);
      setSuccess(result.message || 'Template uploaded — review the preview below, then click Save Branding.');
      setTimeout(() => setSuccess(null), 6000);
    } catch (err) {
      setError(parseApiError(err, 'Template upload failed.'));
    } finally {
      setUploadingHtmlTemplate(false);
      e.target.value = '';
    }
  };

  const handleOffsetChange = async (newTop, newLeft) => {
    const top = Math.min(400, Math.max(0, Number(newTop ?? config.letterhead_top_offset_mm ?? 70)));
    const left = Math.min(200, Math.max(0, Number(newLeft ?? config.letterhead_left_offset_mm ?? 0)));
    setConfig(prev => ({ ...prev, letterhead_top_offset_mm: top, letterhead_left_offset_mm: left }));
    try {
      const result = await updateLetterheadOffset(top, left);
      loadPreview('custom', result.custom_template_s3_key);
    } catch (err) {
      setError(parseApiError(err, 'Failed to update offset.'));
    }
  };

  // ── Apply edited HTML source ──────────────────────────
  const handleApplyHtmlEdit = async () => {
    if (!letterheadHtmlEdited) return;
    try {
      setApplyingHtml(true);
      setError(null);
      const result = await applyLetterheadSource(letterheadHtmlEdited);
      setLetterheadHtml(letterheadHtmlEdited);
      setConfig(prev => ({ ...prev, custom_template_s3_key: result.s3_key }));
      setPreviewTab('preview');
      loadPreview('custom', result.s3_key);
      setSuccess('HTML applied — preview updated.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(parseApiError(err, 'Failed to apply HTML — check that {{ patient.name }} and {{ medicines }} are present.'));
    } finally {
      setApplyingHtml(false);
    }
  };

  const currentCaps = capabilities[config.theme] || {
    supports_letterhead_margins: config.theme === 'custom',
    supports_section_reordering: config.theme === 'custom',
    supports_paper_mode: config.theme === 'custom',
  };

  const handleMoveSection = (index, direction) => {
    const currentSections = config.sections || DEFAULT_SECTIONS;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= currentSections.length) return;
    const updated = [...currentSections];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;
    const reordered = updated.map((sec, idx) => ({ ...sec, order: idx + 1 }));
    setConfig(prev => ({ ...prev, sections: reordered }));
  };

  const handleToggleSection = (index) => {
    const currentSections = config.sections || DEFAULT_SECTIONS;
    const updated = [...currentSections];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setConfig(prev => ({ ...prev, sections: updated }));
  };

  const handleTitleOverrideChange = (index, value) => {
    const currentSections = config.sections || DEFAULT_SECTIONS;
    const updated = [...currentSections];
    updated[index] = { ...updated[index], title_override: value };
    setConfig(prev => ({ ...prev, sections: updated }));
  };

  const handleResetLayout = () => {
    setConfig(prev => ({
      ...prev,
      paper_mode: 'normal',
      show_header: true,
      show_doctor_header: true,
      show_patient_banner: true,
      show_footer: true,
      margins: { top: 6, bottom: 6, left: 7, right: 7 },
      sections: DEFAULT_SECTIONS,
    }));
    setSuccess('Layout settings reset to defaults.');
    setTimeout(() => setSuccess(null), 3000);
  };

  // ── Styles ────────────────────────────────────────────
  const cardBg = isDark ? '#16213e' : '#f8fafc';
  const borderColor = isDark ? '#2a2a4a' : '#e2e8f0';
  const textPrimary = isDark ? '#e2e8f0' : '#1a202c';
  const textSecondary = isDark ? '#a0aec0' : '#718096';

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: textSecondary }}>
        Loading prescription settings...
      </div>
    );
  }

  // ── Page dimensions (px at 96 dpi, 1mm = 3.7795px) ─────────────────────
  const PAGE_PX = { A5: [559, 793], A4: [794, 1122], LETTER: [816, 1054], LEGAL: [816, 1346] };
  const [pagePxW, pagePxH] = PAGE_PX[config.page_size || 'A4'] || PAGE_PX.A4;

  // ── Drag-and-drop helpers ────────────────────────────────────────────────
  // Scale live preview to fit ~360px wide within the right panel
  const LIVE_SCALE = Math.min(360 / pagePxW, 530 / pagePxH);

  // Injects a drag script into the live preview iframe srcDoc.
  // Elements with position:absolute become draggable; on drop the updated HTML
  // is postMessage'd to the parent and reflected in the editor textarea.
  const injectDragScript = (html) => {
    if (!html) return html;
    // Strip any previously injected script + toolbar/handle divs so they're never doubled
    const cleaned = html
      .replace(/<script id="ak-drag"[\s\S]*?<\/script>/g, '')
      .replace(/<div id="ak-tb"[^>]*>[\s\S]*?<\/div>/g, '')
      .replace(/<div id="ak-rh"[^>]*><\/div>/g, '');
    const mmToPx = 96 / 25.4;
    const script = `<script id="ak-drag">
(function(){
  var SCALE=${LIVE_SCALE},mmToPx=${mmToPx.toFixed(4)};
  function unitToPx(v,u){return u==='mm'?parseFloat(v)*mmToPx:parseFloat(v)||0;}
  function pxToUnit(px,u){return u==='mm'?(px/mmToPx).toFixed(1)+'mm':Math.round(px)+'px';}
  function getUnit(s){return s&&s.trim().endsWith('mm')?'mm':'px';}

  function postHtml(){
    tb.remove();rh.remove();
    var h='<!DOCTYPE html>\\n'+document.documentElement.outerHTML;
    document.body.appendChild(tb);document.body.appendChild(rh);
    window.parent.postMessage({type:'ak-drag-update',html:h},'*');
  }

  // ── Toolbar (shown while element is selected) ─────────────────────────────
  var tb=document.createElement('div');
  tb.id='ak-tb';
  tb.style.cssText='position:fixed;z-index:99999;display:none;align-items:center;'+
    'background:#1e293b;color:#f1f5f9;border-radius:8px;font-size:11px;'+
    'font-family:Arial,sans-serif;white-space:nowrap;'+
    'box-shadow:0 4px 16px rgba(0,0,0,0.6);border:1px solid rgba(255,255,255,0.12);overflow:hidden;';

  var lblSpan=document.createElement('span');
  lblSpan.style.cssText='padding:5px 9px;color:#94a3b8;font-size:10px;font-weight:700;'+
    'letter-spacing:0.07em;border-right:1px solid rgba(255,255,255,0.12);user-select:none;';

  var dlBtn=document.createElement('span');
  dlBtn.textContent='✕ Delete';
  dlBtn.style.cssText='padding:5px 10px;cursor:pointer;color:#f87171;user-select:none;';
  dlBtn.title='Remove element';

  tb.appendChild(lblSpan);tb.appendChild(dlBtn);
  document.body.appendChild(tb);

  // ── Resize handle (bottom-centre pill) ────────────────────────────────────
  var rh=document.createElement('div');
  rh.id='ak-rh';
  rh.style.cssText='position:fixed;z-index:100000;display:none;width:32px;height:8px;'+
    'background:#3b82f6;border-radius:4px;cursor:ns-resize;transform:translateX(-50%);'+
    'box-shadow:0 2px 8px rgba(59,130,246,0.55);';
  rh.title='Drag up/down to resize height';
  document.body.appendChild(rh);

  // ── State ──────────────────────────────────────────────────────────────────
  var selected=null,hovered=null,isDragging=false;

  function skip(el){
    if(!el||el===document.body||el===document.documentElement)return true;
    if(el===tb||tb.contains(el))return true;
    if(el===rh)return true;
    var t=el.tagName;
    return t==='SCRIPT'||t==='STYLE'||t==='HTML'||t==='HEAD'||t==='META'||t==='LINK';
  }

  function elLabel(el){
    var c=(el.className||'').trim().split(/\\s+/)[0];
    return c?('.'+c):el.tagName.toLowerCase();
  }

  function placeUI(el){
    var r=el.getBoundingClientRect();
    var tW=tb.offsetWidth||110;
    tb.style.top=Math.max(2,r.top-36)+'px';
    tb.style.left=Math.max(2,r.right-tW)+'px';
    rh.style.top=(r.bottom-4)+'px';
    rh.style.left=(r.left+r.width/2)+'px';
  }

  // ── Select / Deselect ─────────────────────────────────────────────────────
  function selectEl(el){
    if(selected&&selected!==el)selected.style.outline='';
    selected=el;
    el.style.outline='2px solid #3b82f6';
    lblSpan.textContent=elLabel(el).toUpperCase();
    tb.style.display='flex';
    rh.style.display='block';
    placeUI(el);
  }

  function deselect(){
    if(selected){selected.style.outline='';selected=null;}
    tb.style.display='none';rh.style.display='none';
  }

  // ── Hover: thin highlight shows interactivity ──────────────────────────────
  document.addEventListener('mouseover',function(e){
    if(isDragging||skip(e.target))return;
    if(hovered&&hovered!==e.target&&hovered!==selected)hovered.style.outline='';
    hovered=e.target;
    if(e.target!==selected)e.target.style.outline='1px dashed rgba(99,179,237,0.45)';
  },true);

  document.addEventListener('mouseout',function(e){
    if(isDragging||skip(e.target))return;
    if(e.target===hovered&&e.target!==selected){e.target.style.outline='';hovered=null;}
  },true);

  // ── Click to select; drag selected element to move ────────────────────────
  document.addEventListener('mousedown',function(e){
    if(e.button!==0)return;
    // Clicking the toolbar or resize handle — handled by their own listeners
    if(e.target===tb||tb.contains(e.target)||e.target===rh)return;
    // Clicking empty space deselects
    if(skip(e.target)){deselect();return;}

    // If clicking the already-selected element (or a child of it), start drag
    if(selected&&(e.target===selected||selected.contains(e.target))){
      startDrag(e,selected);return;
    }
    // Otherwise select the clicked element
    selectEl(e.target);
    e.stopPropagation();
  },true);

  function startDrag(e,el){
    e.preventDefault();e.stopPropagation();
    var sx=e.clientX,sy=e.clientY,moved=false;
    var pos=window.getComputedStyle(el).position;
    var lV,tV,lU,tU,lPx,tPx,tx,ty,m;

    if(pos==='absolute'||pos==='fixed'){
      lV=el.style.left||'0px';tV=el.style.top||'0px';
      lU=getUnit(lV);tU=getUnit(tV);
      lPx=unitToPx(lV,lU);tPx=unitToPx(tV,tU);
    } else {
      m=(el.style.transform||'').match(/translate\\((-?[\\d.]+)px,\\s*(-?[\\d.]+)px\\)/);
      tx=m?parseFloat(m[1]):0;ty=m?parseFloat(m[2]):0;
      el.style.position='relative';
    }

    function onMove(e){
      var dx=e.clientX-sx,dy=e.clientY-sy;
      if(!moved&&Math.abs(dx)+Math.abs(dy)<4)return;
      if(!moved){moved=true;isDragging=true;tb.style.display='none';rh.style.display='none';}
      el.style.zIndex=9999;
      el.style.outline='2px dashed rgba(59,130,246,0.8)';
      if(pos==='absolute'||pos==='fixed'){
        el.style.left=pxToUnit(lPx+dx/SCALE,lU);
        el.style.top=pxToUnit(tPx+dy/SCALE,tU);
      } else {
        el.style.transform='translate('+(tx+dx/SCALE).toFixed(1)+'px,'+(ty+dy/SCALE).toFixed(1)+'px)';
      }
    }

    function onUp(){
      document.removeEventListener('mousemove',onMove);
      document.removeEventListener('mouseup',onUp);
      el.style.zIndex='';
      if(moved){isDragging=false;selectEl(el);postHtml();}
      else isDragging=false;
    }

    document.addEventListener('mousemove',onMove);
    document.addEventListener('mouseup',onUp);
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  dlBtn.addEventListener('click',function(e){
    e.stopPropagation();
    if(!selected)return;
    selected.style.outline='';selected.remove();
    tb.style.display='none';rh.style.display='none';selected=null;
    postHtml();
  });

  // ── Resize height (bottom-edge handle) ────────────────────────────────────
  // Drag up = shorter, drag down = taller. Works on header and footer alike.
  rh.addEventListener('mousedown',function(e){
    if(e.button!==0||!selected)return;
    e.preventDefault();e.stopPropagation();
    var el=selected;
    isDragging=true;
    tb.style.display='none';rh.style.display='none';
    var hStr=el.style.height;
    var hU=hStr?getUnit(hStr):'mm';
    var hPx=hStr?unitToPx(hStr,hU):el.getBoundingClientRect().height;
    var sy=e.clientY;
    el.style.outline='2px dashed rgba(59,130,246,0.9)';el.style.zIndex=9999;
    function mvH(e){
      el.style.height=pxToUnit(Math.max(2,hPx+(e.clientY-sy)/SCALE),hU);
    }
    function upH(){
      isDragging=false;el.style.zIndex='';
      document.removeEventListener('mousemove',mvH);document.removeEventListener('mouseup',upH);
      selectEl(el);postHtml();
    }
    document.addEventListener('mousemove',mvH);document.addEventListener('mouseup',upH);
  });

  // ESC deselects
  document.addEventListener('keydown',function(e){if(e.key==='Escape')deselect();});
})();
</script>`;
    // Render Jinja2 syntax as styled chips so the live preview looks like a design
    // tool instead of raw template code. Control tags ({% %}) are hidden; variable
    // expressions ({{ }}) become blue chips with the variable name.
    const previewHtml = cleaned
      .replace(/\{%-?[\s\S]*?-?%\}/g, '')
      .replace(/\{\{\s*([\s\S]*?)\s*\}\}/g, (_m, inner) => {
        const label = inner.trim().replace(/\s+/g, ' ').slice(0, 22);
        return `<span style="display:inline-block;background:#dbeafe;color:#1e40af;border:1px solid #93c5fd;border-radius:3px;padding:0 4px;font-size:7.5pt;font-weight:600;white-space:nowrap;vertical-align:middle;max-width:28mm;overflow:hidden;text-overflow:ellipsis;" title="${inner.trim()}">${label}</span>`;
      });

    if (previewHtml.includes('</body>')) return previewHtml.replace('</body>', script + '\n</body>');
    return previewHtml + script;
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '900px',
      margin: '0 auto',
      color: textPrimary,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: textPrimary }}>
          Prescription Branding
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: textSecondary }}>
          Customise how your prescriptions look when printed. Changes apply to all doctors in your clinic.
        </p>
      </div>

      {/* Toast messages */}
      {error && (
        <div style={{
          background: isDark ? '#4a1c1c' : '#fef2f2',
          border: '1px solid #f87171',
          borderRadius: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#f87171',
        }}>
          {error}
          <button onClick={() => setError(null)} style={{
            float: 'right', background: 'none', border: 'none', color: '#f87171',
            cursor: 'pointer', fontSize: '14px',
          }}>✕</button>
        </div>
      )}
      {success && (
        <div style={{
          background: isDark ? '#1a3a2a' : '#f0fdf4',
          border: '1px solid #4ade80',
          borderRadius: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#4ade80',
        }}>
          {success}
        </div>
      )}

      {/* ═══ THEME SELECTION ═══ */}
      <SectionCard title="Theme" subtitle="Choose a prescription layout style" isDark={isDark}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '12px',
        }}>
          {themes.map((theme) => {
            const preview = THEME_PREVIEWS[theme.id] || THEME_PREVIEWS.classic;
            const isSelected = config.theme === theme.id;
            return (
              <div
                key={theme.id}
                onClick={() => setConfig(prev => ({ ...prev, theme: theme.id }))}
                style={{
                  border: `2px solid ${isSelected ? '#0d9488' : borderColor}`,
                  borderRadius: '10px',
                  padding: '14px',
                  cursor: 'pointer',
                  background: isSelected ? (isDark ? '#0d3d38' : '#f0fdfa') : cardBg,
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
              >
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: '8px', right: '8px',
                    background: '#0d9488', color: '#fff', borderRadius: '50%',
                    width: '20px', height: '20px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: '12px',
                  }}>✓</div>
                )}
                <div style={{
                  width: '100%', height: '60px', borderRadius: '6px', marginBottom: '10px',
                  background: preview.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '24px',
                }}>
                  {preview.emoji}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: textPrimary }}>
                  {theme.name}
                </div>
                <div style={{ fontSize: '11px', color: textSecondary, marginTop: '4px', lineHeight: 1.4 }}>
                  {theme.description}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setTemplateVariants([]); loadPreview(theme.id); }}
                  style={{
                    marginTop: '8px',
                    background: 'none',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '6px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    color: textSecondary,
                    cursor: 'pointer',
                  }}
                >
                  Preview
                </button>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* ═══ CLINIC INFO OVERRIDES ═══ */}
      <SectionCard title="Organisation Info" subtitle="Customise organisation details shown on prescriptions. Leave blank to use defaults from your org profile." isDark={isDark}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Organisation Name */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Organisation Name
            </label>
            <input
              type="text"
              placeholder="e.g. Aarogya Health Clinic"
              value={config.clinic_name || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, clinic_name: e.target.value }))}
              style={{
                width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Description */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Description (optional)
            </label>
            <textarea
              placeholder="e.g. A unit of Jeewan Anand Hospital Health Care Pvt. Ltd"
              value={config.clinic_description || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, clinic_description: e.target.value }))}
              maxLength={300}
              rows={2}
              style={{
                width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
                fontFamily: 'inherit', resize: 'vertical',
              }}
            />
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginTop: '4px' }}>
              Printed under the organisation name on the token. Line breaks are preserved.
            </span>
          </div>

          {/* Clinic Address */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Address (Street / Locality)
            </label>
            <input
              type="text"
              placeholder="e.g. 42, MG Road, Koregaon Park"
              value={config.clinic_address || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, clinic_address: e.target.value }))}
              style={{
                width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Clinic Phone */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="e.g. +91 98765 43210"
              value={config.clinic_phone || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, clinic_phone: e.target.value }))}
              maxLength={40}
              style={{
                width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginTop: '4px' }}>
              Enter up to two numbers, separated by a comma. Both print at the bottom of the token.
            </span>
          </div>

          {/* Clinic Email */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Email Address
            </label>
            <input
              type="email"
              placeholder="e.g. contact@clinic.in"
              value={config.clinic_email || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, clinic_email: e.target.value }))}
              style={{
                width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
              }}
            />
          </div>

          {/* PIN Code */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              PIN / ZIP Code
            </label>
            <input
              type="text"
              placeholder="e.g. 411001"
              value={config.clinic_pincode || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, clinic_pincode: e.target.value }))}
              maxLength={10}
              style={{
                width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      </SectionCard>

      {/* ═══ BRANDING OPTIONS ═══ */}
      <SectionCard title="Branding" subtitle="Colours, fonts, and logo" isDark={isDark}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Accent Color */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Accent Colour
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="color"
                value={config.accent_color || '#0d4f8b'}
                onChange={(e) => setConfig(prev => ({ ...prev, accent_color: e.target.value }))}
                style={{ width: '40px', height: '32px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              />
              <input
                type="text"
                value={config.accent_color || '#0d4f8b'}
                onChange={(e) => setConfig(prev => ({ ...prev, accent_color: e.target.value }))}
                style={{
                  flex: 1, padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                  borderRadius: '6px', background: cardBg, color: textPrimary, fontFamily: 'monospace',
                }}
              />
            </div>
          </div>

          {/* Font Family */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Font Family
            </label>
            <select
              value={config.font_family || FONT_OPTIONS[0].value}
              onChange={(e) => setConfig(prev => ({ ...prev, font_family: e.target.value }))}
              style={{
                width: '100%', padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary,
              }}
            >
              {FONT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>

          {/* Page Size */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Page Size
            </label>
            <select
              value={config.page_size || 'A4'}
              onChange={(e) => setConfig(prev => ({ ...prev, page_size: e.target.value }))}
              style={{
                width: '100%', padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', background: cardBg, color: textPrimary,
              }}
            >
              {PAGE_SIZES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Section Backgrounds toggle removed — show_section_backgrounds is not
              accepted by the backend PrescriptionBrandingConfig model (extra="forbid"),
              so it can never persist. Left the config field/default alone (harmless);
              just don't render a control that silently no-ops on save. */}

          {/* Logo Upload */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
              Clinic Logo
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {logoPreviewUrl && (
                <img
                  src={logoPreviewUrl}
                  alt="Logo"
                  style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', border: `1px solid ${borderColor}` }}
                />
              )}
              <label style={{
                padding: '6px 12px', fontSize: '12px', border: `1px solid ${borderColor}`,
                borderRadius: '6px', cursor: 'pointer', color: textSecondary,
                background: cardBg, display: 'inline-block',
              }}>
                {uploadingLogo ? 'Uploading...' : (logoPreviewUrl ? 'Change Logo' : 'Upload Logo')}
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} disabled={uploadingLogo} />
              </label>
              {logoPreviewUrl && (
                <button
                  onClick={() => {
                    URL.revokeObjectURL(logoPreviewUrl);
                    setLogoPreviewUrl(null);
                    setConfig(prev => ({ ...prev, clinic_logo_s3_key: null }));
                  }}
                  style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '12px' }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      {/* ═══ CUSTOM TEMPLATE UPLOAD ═══ */}
      <SectionCard title="Custom Template" subtitle="Upload a scan of your printed prescription pad. Patient data is overlaid automatically." isDark={isDark}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Upload button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{
              padding: '8px 16px', fontSize: '13px', borderRadius: '8px',
              cursor: uploadingLetterhead ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg, #8e44ad, #9b59b6)', color: '#fff',
              fontWeight: 600, display: 'inline-block', opacity: uploadingLetterhead ? 0.7 : 1,
            }}>
              {uploadingLetterhead ? 'Uploading...' : (config.letterhead_raw_s3_key ? 'Replace Letterhead' : 'Upload Letterhead Image')}
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleLetterheadUpload}
                style={{ display: 'none' }}
                disabled={uploadingLetterhead}
              />
            </label>
            {config.letterhead_raw_s3_key && (
              <span style={{ fontSize: '12px', color: '#4ade80' }}>
                ✓ {letterheadGenerationMethod === 'gpt4o' ? 'AI-generated template active' : 'Letterhead active'}
              </span>
            )}
            <span style={{ fontSize: '11px', color: textSecondary }}>PNG or JPG, max 5MB</span>
            {/* ── HTML upload option ── */}
            <span style={{ fontSize: '11px', color: textSecondary, alignSelf: 'center' }}>— or —</span>
            <label style={{
              padding: '8px 16px', fontSize: '13px', borderRadius: '8px',
              cursor: uploadingHtmlTemplate ? 'not-allowed' : 'pointer',
              background: isDark ? '#1e293b' : '#f1f5f9',
              border: `1px solid ${borderColor}`, color: textPrimary,
              fontWeight: 600, display: 'inline-block', opacity: uploadingHtmlTemplate ? 0.7 : 1,
            }}>
              {uploadingHtmlTemplate ? 'Uploading...' : (
                config.custom_template_s3_key && !config.letterhead_raw_s3_key
                  ? 'Replace HTML Template'
                  : 'Upload HTML Template'
              )}
              <input
                type="file"
                accept=".html,text/html"
                onChange={handleHtmlTemplateUpload}
                style={{ display: 'none' }}
                disabled={uploadingHtmlTemplate}
              />
            </label>
            <span style={{ fontSize: '11px', color: textSecondary }}>Jinja2 HTML, max 500KB</span>
          </div>

          {/* ── Legacy migration banner ── */}
          {config.theme === 'custom' && config.template_mode === 'legacy_html' && (
            <div style={{
              background: isDark ? '#451a03' : '#fffbeb',
              border: `1px solid ${isDark ? '#92400e' : '#f59e0b'}`,
              borderRadius: '8px', padding: '10px 14px',
              display: 'flex', alignItems: 'flex-start', gap: '10px',
            }}>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#fcd34d' : '#92400e', marginBottom: '3px' }}>
                  Legacy template format
                </div>
                <div style={{ fontSize: '12px', color: isDark ? '#fde68a' : '#78350f', lineHeight: 1.5 }}>
                  This template renders HTML on every prescription (WeasyPrint). Re-upload with
                  the <strong>static_fill_content</strong> contract (add <code>ak-content-*</code> meta
                  tags, remove patient/medicines variables) for faster generation and better scaling.
                </div>
              </div>
            </div>
          )}

          {/* ── Structured upload validation errors ── */}
          {uploadErrors.length > 0 && (
            <div style={{
              background: isDark ? '#450a0a' : '#fef2f2',
              border: `1px solid ${isDark ? '#991b1b' : '#fca5a5'}`,
              borderRadius: '8px', padding: '12px 14px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#fca5a5' : '#991b1b', marginBottom: '8px' }}>
                Template validation failed
              </div>
              {uploadErrors.map((err, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px',
                  fontSize: '12px', color: isDark ? '#fca5a5' : '#7f1d1d', marginBottom: '4px',
                }}>
                  <span style={{ flexShrink: 0, color: '#ef4444' }}>✕</span>
                  {err.type === 'forbidden_dynamic_variable' && (
                    <span>Remove dynamic variable <code>{err.variable}</code> — prescription data is injected by fill_content, not the template.</span>
                  )}
                  {err.type === 'forbidden_loop' && (
                    <span>Remove loop <code>{err.variable}</code> — medicine rendering is handled by fill_content.</span>
                  )}
                  {err.type === 'forbidden_conditional' && (
                    <span>Remove conditional <code>{err.variable}</code> — patient logic belongs in fill_content.</span>
                  )}
                  {err.type === 'missing_meta_tag' && (
                    <span>Missing required meta tag: <code>&lt;meta name="{err.tag}" content="..."&gt;</code></span>
                  )}
                  {!['forbidden_dynamic_variable','forbidden_loop','forbidden_conditional','missing_meta_tag'].includes(err.type) && (
                    <span>{err.variable || err.tag || JSON.stringify(err)}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Upload warnings ── */}
          {uploadWarnings.length > 0 && (
            <div style={{
              background: isDark ? '#1c1917' : '#fffbeb',
              border: `1px solid ${isDark ? '#78350f' : '#fcd34d'}`,
              borderRadius: '6px', padding: '10px 14px',
            }}>
              {uploadWarnings.map((w, i) => (
                <div key={i} style={{ fontSize: '12px', color: isDark ? '#fde68a' : '#92400e' }}>⚠ {w}</div>
              ))}
            </div>
          )}

          {/* ── Static template content box visualisation ── */}
          {config.template_mode === 'static_fill_content' && contentBox && (
            <div style={{
              background: isDark ? '#0f172a' : '#f0fdf4',
              border: `1px solid ${isDark ? '#166534' : '#86efac'}`,
              borderRadius: '8px', padding: '12px 14px',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: isDark ? '#86efac' : '#166534', marginBottom: '8px' }}>
                ✓ Static template detected — writable zone
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                {[
                  { label: 'Left', value: contentBox.left_mm },
                  { label: 'Top', value: contentBox.top_mm },
                  { label: 'Width', value: contentBox.width_mm },
                  { label: 'Height', value: contentBox.height_mm },
                ].map(({ label, value }) => (
                  <div key={label} style={{
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${isDark ? '#334155' : '#bbf7d0'}`,
                    borderRadius: '6px', padding: '6px 10px', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '10px', color: textSecondary, marginBottom: '2px' }}>{label}</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: textPrimary }}>{value}mm</div>
                  </div>
                ))}
              </div>
              {contentBox.height_mm < 80 && (
                <div style={{ fontSize: '11px', color: isDark ? '#fde68a' : '#92400e', marginTop: '8px' }}>
                  ⚠ Content height ({contentBox.height_mm}mm) is small — some prescriptions may be clipped.
                </div>
              )}
              <div style={{ fontSize: '11px', color: textSecondary, marginTop: '6px' }}>
                fill_content() draws patient data and medicines inside this zone. Template provides the branding background.
              </div>
            </div>
          )}

          {/* Offset controls — always shown after upload */}
          {config.letterhead_raw_s3_key && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, whiteSpace: 'nowrap' }}>
                    Top
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={400}
                    step={1}
                    value={config.letterhead_top_offset_mm ?? 70}
                    onChange={(e) => handleOffsetChange(e.target.value, null)}
                    style={{
                      width: '70px', padding: '4px 8px', fontSize: '13px',
                      border: `1px solid ${borderColor}`, borderRadius: '6px',
                      background: cardBg, color: textPrimary,
                    }}
                  />
                  <span style={{ fontSize: '12px', color: textSecondary }}>mm</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, whiteSpace: 'nowrap' }}>
                    Left
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={200}
                    step={1}
                    value={config.letterhead_left_offset_mm ?? 0}
                    onChange={(e) => handleOffsetChange(null, e.target.value)}
                    style={{
                      width: '70px', padding: '4px 8px', fontSize: '13px',
                      border: `1px solid ${borderColor}`, borderRadius: '6px',
                      background: cardBg, color: textPrimary,
                    }}
                  />
                  <span style={{ fontSize: '12px', color: textSecondary }}>mm</span>
                </div>
              </div>
              <span style={{ fontSize: '11px', color: textSecondary }}>
                Top: where prescription data starts vertically. Left: skip the doctor panel column.
              </span>
            </div>
          )}

          {/* Detected doctor/clinic metadata */}
          {letterheadMetadata && letterheadGenerationMethod === 'gpt4o' && (
            <div style={{
              fontSize: '12px',
              background: isDark ? '#1a1f2e' : '#f8fafc',
              border: `1px solid ${borderColor}`,
              borderRadius: '6px',
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
            }}>
              <span style={{ fontWeight: 600, color: textPrimary, marginBottom: '4px' }}>
                Detected from letterhead
              </span>
              {letterheadMetadata.clinic_name && (
                <span style={{ color: textSecondary }}>Clinic: {letterheadMetadata.clinic_name}</span>
              )}
              {letterheadMetadata.doctor_name && (
                <span style={{ color: textSecondary }}>Doctor: {letterheadMetadata.doctor_name}</span>
              )}
              {letterheadMetadata.qualifications && (
                <span style={{ color: textSecondary }}>Qualifications: {letterheadMetadata.qualifications}</span>
              )}
              {letterheadMetadata.address && (
                <span style={{ color: textSecondary }}>Address: {letterheadMetadata.address}</span>
              )}
              {letterheadMetadata.phone_numbers?.length > 0 && (
                <span style={{ color: textSecondary }}>Phone: {letterheadMetadata.phone_numbers.join(', ')}</span>
              )}
              {letterheadMetadata.website && (
                <span style={{ color: textSecondary }}>Website: {letterheadMetadata.website}</span>
              )}
            </div>
          )}
        </div>
      </SectionCard>

      {/* ═══ PAPER MODE & PRINT MARGINS (Custom Theme Only) ═══ */}
      {(currentCaps.supports_paper_mode || currentCaps.supports_letterhead_margins) && (
        <SectionCard title="Paper Mode & Print Margins" subtitle="Configure reserved blank spacing for physical pre-printed letterhead paper." isDark={isDark}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Paper Mode Cards */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '8px' }}>
                Paper Mode
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div
                  onClick={() => setConfig(prev => ({ ...prev, paper_mode: 'normal', show_header: true, show_doctor_header: true, show_footer: true }))}
                  style={{
                    border: `2px solid ${config.paper_mode !== 'preprinted' ? '#0d9488' : borderColor}`,
                    borderRadius: '10px', padding: '12px 14px', cursor: 'pointer',
                    background: config.paper_mode !== 'preprinted' ? (isDark ? '#0d3d38' : '#f0fdfa') : cardBg,
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 700, color: textPrimary }}>📄 Standard Digital</div>
                  <div style={{ fontSize: '11px', color: textSecondary, marginTop: '3px' }}>
                    Prints clinic header logo, address, and signature on digital paper.
                  </div>
                </div>
                <div
                  onClick={() => setConfig(prev => ({ ...prev, paper_mode: 'preprinted', show_header: false, show_doctor_header: false, show_footer: false }))}
                  style={{
                    border: `2px solid ${config.paper_mode === 'preprinted' ? '#0d9488' : borderColor}`,
                    borderRadius: '10px', padding: '12px 14px', cursor: 'pointer',
                    background: config.paper_mode === 'preprinted' ? (isDark ? '#0d3d38' : '#f0fdfa') : cardBg,
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 700, color: textPrimary }}>📑 Pre-printed Letterhead</div>
                  <div style={{ fontSize: '11px', color: textSecondary, marginTop: '3px' }}>
                    Leaves reserved top/bottom spaces blank for physical pad paper.
                  </div>
                </div>
              </div>
            </div>

            {/* Margin Inputs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
                  Top Space (mm)
                </label>
                <input
                  type="number"
                  min={0}
                  max={120}
                  step={1}
                  value={config.margins?.top ?? 6}
                  onChange={(e) => {
                    const top = Number(e.target.value);
                    setConfig(prev => ({ ...prev, margins: { ...prev.margins, top } }));
                  }}
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                    borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
                  Bottom Space (mm)
                </label>
                <input
                  type="number"
                  min={0}
                  max={120}
                  step={1}
                  value={config.margins?.bottom ?? 6}
                  onChange={(e) => {
                    const bottom = Number(e.target.value);
                    setConfig(prev => ({ ...prev, margins: { ...prev.margins, bottom } }));
                  }}
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                    borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
                  Left Margin (mm)
                </label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  step={1}
                  value={config.margins?.left ?? 7}
                  onChange={(e) => {
                    const left = Number(e.target.value);
                    setConfig(prev => ({ ...prev, margins: { ...prev.margins, left } }));
                  }}
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                    borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px' }}>
                  Right Margin (mm)
                </label>
                <input
                  type="number"
                  min={0}
                  max={50}
                  step={1}
                  value={config.margins?.right ?? 7}
                  onChange={(e) => {
                    const right = Number(e.target.value);
                    setConfig(prev => ({ ...prev, margins: { ...prev.margins, right } }));
                  }}
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '13px', border: `1px solid ${borderColor}`,
                    borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Header/Footer/Banner Toggles */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: textPrimary }}>
                <input
                  type="checkbox"
                  checked={config.show_header !== false}
                  onChange={(e) => setConfig(prev => ({ ...prev, show_header: e.target.checked }))}
                />
                Print Clinic Header (Left)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: textPrimary }}>
                <input
                  type="checkbox"
                  checked={config.show_doctor_header !== false}
                  onChange={(e) => setConfig(prev => ({ ...prev, show_doctor_header: e.target.checked }))}
                />
                Print Doctor Header (Right)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: textPrimary }}>
                <input
                  type="checkbox"
                  checked={config.show_patient_banner !== false}
                  onChange={(e) => setConfig(prev => ({ ...prev, show_patient_banner: e.target.checked }))}
                />
                Print Patient Info Banner
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: textPrimary }}>
                <input
                  type="checkbox"
                  checked={config.show_footer !== false}
                  onChange={(e) => setConfig(prev => ({ ...prev, show_footer: e.target.checked }))}
                />
                Print Doctor Signature (Bottom Right)
              </label>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ═══ SECTION ARRANGEMENT & VISIBILITY (Custom Theme Only) ═══ */}
      {currentCaps.supports_section_reordering && (
        <SectionCard title="Section Arrangement & Visibility" subtitle="Re-order or toggle prescription sections for custom templates." isDark={isDark}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {(config.sections || DEFAULT_SECTIONS).map((sec, idx) => (
              <div
                key={sec.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: '8px',
                  border: `1px solid ${borderColor}`, background: isDark ? '#1e293b' : '#f8fafc',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <button
                      onClick={() => handleMoveSection(idx, -1)}
                      disabled={idx === 0}
                      style={{
                        border: 'none', background: 'none', cursor: idx === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '12px', color: idx === 0 ? '#cbd5e1' : textPrimary, padding: '0 4px',
                      }}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleMoveSection(idx, 1)}
                      disabled={idx === (config.sections || DEFAULT_SECTIONS).length - 1}
                      style={{
                        border: 'none', background: 'none', cursor: idx === (config.sections || DEFAULT_SECTIONS).length - 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px', color: idx === (config.sections || DEFAULT_SECTIONS).length - 1 ? '#cbd5e1' : textPrimary, padding: '0 4px',
                      }}
                    >
                      ▼
                    </button>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, width: '20px' }}>
                    #{idx + 1}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: textPrimary }}>
                    {sec.label || DEFAULT_SECTIONS.find(d => d.id === sec.id)?.label || sec.id}
                  </span>
                  {sec.id === 'surgeries' && (
                    <span
                      title={BRANDING_SECTION_SURGERIES_TOOLTIP}
                      aria-label={BRANDING_SECTION_SURGERIES_TOOLTIP}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '16px', height: '16px', borderRadius: '50%', cursor: 'help',
                        fontSize: '10px', fontWeight: 700, color: textSecondary,
                        border: `1px solid ${borderColor}`, background: cardBg,
                      }}
                    >
                      i
                    </span>
                  )}
                  <input
                    type="text"
                    value={sec.title_override || ''}
                    onChange={(e) => handleTitleOverrideChange(idx, e.target.value)}
                    placeholder={DEFAULT_SECTIONS.find(d => d.id === sec.id)?.label || sec.id}
                    aria-label={`Title override for ${sec.label || sec.id}`}
                    style={{
                      width: '160px', padding: '4px 8px', fontSize: '12px',
                      border: `1px solid ${borderColor}`, borderRadius: '6px',
                      background: cardBg, color: textPrimary,
                    }}
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: textSecondary }}>
                  <input
                    type="checkbox"
                    checked={sec.enabled !== false}
                    onChange={() => handleToggleSection(idx)}
                  />
                  {sec.enabled !== false ? 'Enabled' : 'Hidden'}
                </label>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
              <button
                onClick={handleResetLayout}
                style={{
                  background: 'none', border: `1px solid ${borderColor}`, borderRadius: '6px',
                  padding: '6px 12px', fontSize: '12px', color: textSecondary, cursor: 'pointer',
                }}
              >
                Reset Layout Defaults
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ═══ PREVIEW PANEL ═══ */}
      {previewHtml && (
        <SectionCard title={`Preview: ${previewTheme || 'Current'}`} subtitle="Sample prescription with dummy data" isDark={isDark}>
          {/* Tab bar — only shown for legacy/ai_generated templates with editable HTML source */}
          {letterheadHtml && config.template_mode !== 'static_fill_content' && (
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {['preview', 'html'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setPreviewTab(tab)}
                  style={{
                    padding: '6px 16px', fontSize: '12px', fontWeight: 600,
                    borderRadius: '6px', border: `1px solid ${borderColor}`,
                    cursor: 'pointer',
                    background: previewTab === tab
                      ? (isDark ? '#3b82f6' : '#2563eb')
                      : (isDark ? '#1e293b' : '#f8fafc'),
                    color: previewTab === tab ? '#fff' : textPrimary,
                  }}
                >
                  {tab === 'preview' ? 'Preview' : 'HTML Source'}
                </button>
              ))}
            </div>
          )}
          {/* Static template: read-only info strip (no HTML editing) */}
          {config.template_mode === 'static_fill_content' && previewHtml && (
            <div style={{
              fontSize: '12px', color: textSecondary, marginBottom: '10px',
              padding: '6px 10px', background: isDark ? '#1e293b' : '#f8fafc',
              border: `1px solid ${borderColor}`, borderRadius: '6px',
            }}>
              Static template — HTML editing disabled. Use <strong>Replace HTML Template</strong> to upload a new version.
            </div>
          )}

          {previewTab === 'preview' || !letterheadHtml ? (
            <div>
              {/* ── Style variant thumbnail gallery (shown after pipeline upload) ── */}
              {templateVariants.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{
                    fontSize: '11px', fontWeight: 700, color: textSecondary,
                    letterSpacing: '0.06em', marginBottom: '8px', textTransform: 'uppercase',
                  }}>
                    Choose a style
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {templateVariants.map((v) => {
                      const isActive = selectedVariantId === v.variant_id;
                      const VARIANT_GRADIENTS = {
                        standard:     'linear-gradient(135deg, #1a5276 0%, #2980b9 100%)',
                        professional: 'linear-gradient(135deg, #1e3a5f 0%, #2e86ab 50%, #5ba4cf 100%)',
                        minimal:      isDark ? 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                      };
                      return (
                        <button
                          key={v.variant_id}
                          onClick={async () => {
                            setSelectedVariantId(v.variant_id);
                            if (v.s3_key) {
                              // Standard: s3 key already exists — no new upload needed
                              setPendingVariantId(null);
                              setConfig(prev => ({ ...prev, custom_template_s3_key: v.s3_key }));
                              loadPreview('custom', v.s3_key);
                            } else {
                              // Professional / Minimal: build preview in-memory, upload only on Save
                              setPendingVariantId(v.variant_id);
                              try {
                                setPreviewLoading(true);
                                const html = await getVariantPreview(v.variant_id);
                                setPreviewHtml(html);
                              } catch (err) {
                                console.error('[Branding] Variant preview failed:', err);
                                setPreviewHtml('<p style="color:red;padding:20px;">Preview failed to load.</p>');
                              } finally {
                                setPreviewLoading(false);
                              }
                            }
                          }}
                          style={{
                            border: `2px solid ${isActive ? '#0d9488' : borderColor}`,
                            borderRadius: '10px',
                            padding: '10px',
                            cursor: 'pointer',
                            background: isActive ? (isDark ? '#0d3d38' : '#f0fdfa') : (isDark ? '#1e293b' : '#f8fafc'),
                            textAlign: 'left',
                            transition: 'border-color 0.15s, background 0.15s',
                          }}
                        >
                          {/* Mini swatch instead of live iframe thumbnail to keep it fast */}
                          <div style={{
                            width: '100%', height: '52px', borderRadius: '6px',
                            background: VARIANT_GRADIENTS[v.variant_id] || VARIANT_GRADIENTS.standard,
                            marginBottom: '8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '18px',
                            border: v.variant_id === 'minimal' ? `1px solid ${borderColor}` : 'none',
                          }}>
                            {v.variant_id === 'standard' ? '🎨' : v.variant_id === 'professional' ? '✦' : '◻'}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: textPrimary }}>{v.label}</div>
                              <div style={{ fontSize: '11px', color: textSecondary, marginTop: '2px', lineHeight: 1.3 }}>{v.description}</div>
                            </div>
                            {isActive && (
                              <div style={{
                                width: '20px', height: '20px', borderRadius: '50%',
                                background: '#0d9488', color: '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '11px', fontWeight: 700, flexShrink: 0, marginLeft: '6px',
                              }}>✓</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '11px', color: textSecondary, marginTop: '6px' }}>
                    Click a style to preview it below. Your choice is saved when you click Save Branding.
                  </div>
                </div>
              )}

              {/* ── Full preview ── */}
              <div style={{
                border: `1px solid ${borderColor}`, borderRadius: '8px',
                overflow: 'hidden', background: '#fff',
              }}>
              {previewLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: textSecondary }}>Loading preview...</div>
              ) : (
                <div style={{ background: '#e5e7eb', padding: '12px', display: 'flex', justifyContent: 'center' }}>
                  <div style={{
                    width: `${Math.round(pagePxW * 0.9)}px`,
                    height: `${Math.round(pagePxH * 0.9)}px`,
                    overflow: 'hidden', flexShrink: 0,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
                  }}>
                    <div style={{ transform: 'scale(0.9)', transformOrigin: 'top left', width: `${pagePxW}px` }}>
                      <iframe
                        ref={previewFrameRef}
                        srcDoc={previewHtml}
                        title="Prescription Preview"
                        style={{ width: `${pagePxW}px`, height: `${pagePxH}px`, border: 'none', display: 'block' }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          ) : (
            /* Split view: editor left, live preview right */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '12px', height: '580px' }}>

                {/* ── Left: code editor ── */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <div style={{
                    fontSize: '11px', fontWeight: 600, color: textSecondary,
                    marginBottom: '4px', letterSpacing: '0.04em',
                  }}>
                    HTML EDITOR
                  </div>
                  <textarea
                    value={letterheadHtmlEdited ?? ''}
                    onChange={e => setLetterheadHtmlEdited(e.target.value)}
                    spellCheck={false}
                    style={{
                      flex: 1, fontFamily: '"Fira Code", "Cascadia Code", Consolas, monospace',
                      fontSize: '11px', lineHeight: '1.6', tabSize: 2,
                      background: '#0f172a', color: '#e2e8f0',
                      border: `1px solid ${borderColor}`, borderRadius: '8px',
                      padding: '12px', resize: 'none', boxSizing: 'border-box', width: '100%',
                    }}
                  />
                </div>

                {/* ── Right: live preview (debounced, renders HTML directly) ── */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <div style={{
                    fontSize: '11px', fontWeight: 600, color: textSecondary,
                    marginBottom: '4px', letterSpacing: '0.04em',
                  }}>
                    LIVE PREVIEW <span style={{ fontWeight: 400 }}>(Jinja2 vars shown as-is)</span>
                  </div>
                  <div style={{
                    width: '100%',
                    maxWidth: `${Math.round(pagePxW * LIVE_SCALE) + 2}px`,
                    height: `${Math.round(pagePxH * LIVE_SCALE) + 24}px`,
                    border: `1px solid ${borderColor}`, borderRadius: '8px',
                    overflow: 'hidden', background: '#e5e7eb', position: 'relative',
                  }}>
                    <div style={{ fontSize: '10px', color: '#6b7280', padding: '3px 6px', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                      Click to select · Drag to move · Drag blue pill to resize
                    </div>
                    <iframe
                      srcDoc={injectDragScript(debouncedHtml) ?? ''}
                      title="Live HTML Preview"
                      sandbox="allow-same-origin allow-scripts"
                      style={{
                        border: 'none', display: 'block',
                        width: `${pagePxW}px`, height: `${pagePxH}px`,
                        transform: `scale(${LIVE_SCALE})`,
                        transformOrigin: 'top left',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* ── Action buttons ── */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: textSecondary, marginRight: 'auto' }}>
                  Preview updates as you type. Apply Changes uploads to S3 and renders with dummy data.
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(letterheadHtmlEdited ?? '');
                    setSuccess('Copied to clipboard.');
                    setTimeout(() => setSuccess(null), 2000);
                  }}
                  style={{
                    padding: '7px 14px', fontSize: '12px', fontWeight: 600,
                    border: `1px solid ${borderColor}`, borderRadius: '6px',
                    background: isDark ? '#1e293b' : '#f8fafc', color: textPrimary, cursor: 'pointer',
                  }}
                >
                  Copy
                </button>
                <button
                  onClick={() => setLetterheadHtmlEdited(letterheadHtml)}
                  style={{
                    padding: '7px 14px', fontSize: '12px', fontWeight: 600,
                    border: `1px solid ${borderColor}`, borderRadius: '6px',
                    background: isDark ? '#1e293b' : '#f8fafc', color: textPrimary, cursor: 'pointer',
                  }}
                >
                  Reset
                </button>
                <button
                  onClick={handleApplyHtmlEdit}
                  disabled={applyingHtml}
                  style={{
                    padding: '7px 20px', fontSize: '12px', fontWeight: 600,
                    border: 'none', borderRadius: '6px', cursor: applyingHtml ? 'not-allowed' : 'pointer',
                    background: applyingHtml ? '#6b7280' : 'linear-gradient(135deg, #0d9488, #14b8a6)',
                    color: '#fff',
                  }}
                >
                  {applyingHtml ? 'Applying…' : 'Apply & Save to S3'}
                </button>
              </div>
            </div>
          )}
        </SectionCard>
      )}

      {/* ═══ SAVE BUTTON ═══ */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: '12px',
        marginTop: '20px', paddingTop: '16px',
        borderTop: `1px solid ${borderColor}`,
      }}>
        <button
          onClick={() => loadPreview(config.theme, config.custom_template_s3_key)}
          disabled={previewLoading}
          style={{
            padding: '10px 20px', fontSize: '13px', fontWeight: 600,
            border: `1px solid ${borderColor}`, borderRadius: '8px',
            background: cardBg, color: textPrimary, cursor: 'pointer',
          }}
        >
          {previewLoading ? 'Loading...' : 'Preview Current Config'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px', fontSize: '13px', fontWeight: 600,
            border: 'none', borderRadius: '8px', cursor: 'pointer',
            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
            color: '#fff',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </div>
    </div>
  );
}

/* ── Section Card helper ────────────────────────────────────────── */
function SectionCard({ title, subtitle, isDark, children }) {
  const cardBg = isDark ? '#16213e' : '#ffffff';
  const borderColor = isDark ? '#2a2a4a' : '#e2e8f0';
  const textPrimary = isDark ? '#e2e8f0' : '#1a202c';
  const textSecondary = isDark ? '#a0aec0' : '#718096';

  return (
    <div style={{
      background: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '18px',
      marginBottom: '16px',
    }}>
      <h3 style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: 700, color: textPrimary }}>{title}</h3>
      {subtitle && <p style={{ margin: '0 0 14px', fontSize: '12px', color: textSecondary }}>{subtitle}</p>}
      {children}
    </div>
  );
}
